/**
 * Group model interfaces matching the Group API specification
 * 
 * @remarks
 * These models correspond to the Group API endpoints at `/api/groups`
 * All interfaces are designed to match the API response structure exactly.
 * 
 * @see GROUP_API_REFERENCE.md for complete API documentation
 */

import { ActivatableEntity, UserRef, OrganizationRef } from './base';

/**
 * Core Group model matching API schema
 * 
 * @remarks
 * This model represents a group with non-populated references.
 * For populated version with expanded user and org details, use GroupPopulated.
 */
export interface Group extends ActivatableEntity {
  /**
   * Group name (2-100 characters)
   * Must be unique within the organization
   */
  name: string;

  /**
   * Optional group description (max 500 characters)
   */
  description?: string;

  /**
   * ID of the organization this group belongs to
   */
  organizationId: string;

  /**
   * ID of the user who created the group
   */
  createdBy: string;

  /**
   * Array of user IDs who are members of this group
   */
  members: string[];
}

/**
 * Group with populated related entities
 * 
 * @remarks
 * Returned by most Group API endpoints
 * Contains expanded user and organization details
 */
export interface GroupPopulated extends Omit<Group, 'organizationId' | 'createdBy' | 'members'> {
  /**
   * Populated organization reference
   */
  organizationId: OrganizationRef;

  /**
   * Populated creator user reference
   */
  createdBy: UserRef;

  /**
   * Array of populated user references for group members
   */
  members: UserRef[];
}

/**
 * Projected group shape returned in user profile responses
 * 
 * @remarks
 * Returned as part of GET /api/users/profile/detailed.
 * Excludes `createdBy` and `members` (use GET /api/groups/:id for full details).
 */
export type GroupDetailed = Omit<GroupPopulated, 'createdBy' | 'members'>;

/**
 * Data Transfer Object for creating a new group
 * 
 * @remarks
 * Used in POST /api/groups
 * The authenticated user automatically becomes the first member.
 */
export interface CreateGroupDto {
  /**
   * Group name (required, 2-100 characters)
   * Must be unique within the organization
   */
  name: string;

  /**
   * Organization ID this group belongs to (required)
   * User must be a member of this organization
   */
  organizationId: string;

  /**
   * Optional group description (max 500 characters)
   */
  description?: string;
}

/**
 * Data Transfer Object for updating a group
 * 
 * @remarks
 * Used in PUT /api/groups/:id
 * All fields are optional - only provide fields to update.
 */
export interface UpdateGroupDto {
  /**
   * Updated group name (2-100 characters)
   * Must remain unique within the organization
   */
  name?: string;

  /**
   * Updated group description (max 500 characters)
   */
  description?: string;
}

/**
 * Data Transfer Object for adding a member to a group
 * 
 * @remarks
 * Used in POST /api/groups/:id/members
 */
export interface AddGroupMemberDto {
  /**
   * User ID to add to the group (required)
   * User must be a member of the group's organization
   */
  userId: string;
}

/**
 * Query parameters for group search and filtering
 * 
 * @remarks
 * Used in GET /api/groups
 */
export interface GroupQueryParams {
  /**
   * Filter by organization ID
   * If provided, returns only groups from that organization
   * If omitted, returns groups from all user's organizations
   */
  organizationId?: string;

  /**
   * Additional dynamic parameters
   */
  [key: string]: string | number | boolean | undefined;
}

/**
 * API response wrapper for single group
 */
export interface GroupResponse {
  success: boolean;
  data: GroupPopulated;
  message?: string;
}

/**
 * API response wrapper for group list
 */
export interface GroupListResponse {
  success: boolean;
  data: GroupPopulated[];
  count?: number;
  message?: string;
}

/**
 * API response wrapper for group creation
 */
export interface CreateGroupResponse {
  success: boolean;
  data: GroupPopulated;
  message: 'Group created successfully';
}

/**
 * API response wrapper for group update
 */
export interface UpdateGroupResponse {
  success: boolean;
  data: GroupPopulated;
  message: 'Group updated successfully';
}

/**
 * API response wrapper for group deletion
 */
export interface DeleteGroupResponse {
  success: boolean;
  message: 'Group deleted successfully';
}

/**
 * API response wrapper for adding a group member
 */
export interface AddGroupMemberResponse {
  success: boolean;
  data: GroupPopulated;
  message: 'Member added successfully';
}

/**
 * API response wrapper for removing a group member
 */
export interface RemoveGroupMemberResponse {
  success: boolean;
  data: GroupPopulated;
  message: 'Member removed successfully';
}
